import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PricelistControllerApiService, PricelistPrivateControllerApiService } from '@lx/shared/api';
import { getLocale } from '@lx/shared/i18n';
import { getCompanyHeader } from '@lx/shared/tokens';
import { areArraysEqual } from '@lx/shared/utils';
import { ComponentStore } from '@ngrx/component-store';
import { AsyncSubject, catchError, finalize, Observable, switchMap, tap, timeout } from 'rxjs';
import { PriceList, PriceListYears } from '../../models';
import { Company, CompanyEnum } from '../../models/company.model';
import { PriceListTermsLanguage } from '../../models/pricelist-terms-language.mode';
import { AyaSchoolService } from '../../services/aya-school.service';
import { PRIVATE_BASE_PATH } from '../../tokens/private-base-path.token';
import { SharedDataState } from './shared-data.state';

@Injectable({ providedIn: 'root' })
export class SharedDataStore extends ComponentStore<SharedDataState> {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();
  private readonly redirectTimeout = 1000;

  private readonly privateBasePath = inject(PRIVATE_BASE_PATH);
  private readonly httpClient = inject(HttpClient);
  private readonly priceListService = inject(PricelistControllerApiService);
  private readonly privatePriceListService = inject(PricelistPrivateControllerApiService);
  private readonly ayaSchoolService = inject(AyaSchoolService);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super({
      company: CompanyEnum.KIL,
    });
  }

  // Selectors
  readonly company$ = this.select(({ company }) => company);
  readonly priceListArea$ = this.select(({ priceListArea }) => priceListArea);
  readonly priceListYears$ = this.select(({ priceListYears }) => priceListYears);
  readonly currentPriceListYear$ = this.select(({ currentPriceListYear }) => currentPriceListYear);
  readonly privateAccessAndCurrentPriceListYear$ = this.select(
    ({ privateAccess, currentPriceListYear }) => ({
      privateAccess,
      currentPriceListYear,
    }),
    { debounce: true },
  );
  readonly privateAccess$ = this.select(({ privateAccess }) => privateAccess);
  readonly juniorCampsPriceLists$ = this.select(({ juniorCampsPriceLists }) => juniorCampsPriceLists);
  readonly youngAdultsPriceLists$ = this.select(({ youngAdultsPriceLists }) => youngAdultsPriceLists);
  readonly onlineCourses$ = this.select(({ onlineCourses }) => onlineCourses);
  readonly publicHolidays$ = this.select(({ publicHolidays }) => publicHolidays);
  readonly termsAndConditionsKIL$ = this.select(({ termsAndConditionsKIL }) => termsAndConditionsKIL);
  readonly termsAndConditionsALP$ = this.select(({ termsAndConditionsALP }) => termsAndConditionsALP);
  readonly ayaSchoolTables$ = this.select(({ ayaPriceLists }) => this.ayaSchoolService.groupSchoolsByLanguageAndCountryToTables(ayaPriceLists?.schools), {
    debounce: true,
    equal: (previous, current) => areArraysEqual(previous, current),
  });

  readonly priceListsForCompany = (company: Company): Observable<PriceList[] | undefined> =>
    this.select(({ priceLists }) => (priceLists ? priceLists[company] : undefined));

  readonly termsAndConditionsLanguagesForCompany = (company: Company): Observable<PriceListTermsLanguage[] | undefined> =>
    this.select(({ termsAndConditionsLanguages }) => termsAndConditionsLanguages?.filter((language) => language.company === company));

  // Updaters
  readonly updateState = this.updater((state, partialState: Partial<SharedDataState>) => ({
    ...state,
    ...partialState,
  }));

  readonly updateCompany = this.updater((state, company: Company) => ({
    ...state,
    company,
  }));

  /**
   * Loads the shared data (price list years, vpn path, current year) and updates the state with the loaded data.
   *
   * Returns an observable and when the data is loaded, it completes the observable.
   *
   * This method is used to load the shared data when the application starts.
   */
  loadSharedData(): Observable<boolean> {
    const loaded$ = new AsyncSubject<boolean>();

    const fetchPrivateYears$ = this.privatePriceListService
      .getPrivatePriceListYears(this.locale, this.companyHeader)
      .pipe(tap((years) => this.handleYearsResponse(years, true)));

    const fetchPublicYears$ = this.priceListService
      .getPriceListYears(this.locale, this.companyHeader)
      .pipe(tap((years) => this.handleYearsResponse(years, false)));

    this.httpClient
      .head(`${this.privateBasePath}/private/ping`, { observe: 'response' })
      .pipe(
        timeout(this.redirectTimeout),
        switchMap((response) => {
          const vpnAvailable = response.status === 200;
          return vpnAvailable ? fetchPrivateYears$ : fetchPublicYears$;
        }),
        catchError(() => fetchPublicYears$),
        finalize(() => {
          loaded$.next(true);
          loaded$.complete();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    return loaded$.asObservable();
  }

  private handleYearsResponse(priceListYears: PriceListYears, privateAccess: boolean): true {
    const years = priceListYears?.years ?? [];
    const currentYear = years.find((y) => y.current)?.year;

    this.updateState({
      priceListYears: years,
      currentPriceListYear: currentYear,
      privateAccess,
    });

    return true;
  }
}
